import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function IncomeTaxCalculator() {
  useEffect(() => {
    document.title = "Income Tax Calculator 2025 | Free Federal Tax Estimator - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Calculate your 2025 federal income tax with our free tax calculator. Estimate your tax liability, refund, or amount owed based on current IRS tax brackets. Supports all filing statuses including single, married, and head of household.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'income tax calculator, federal tax calculator, tax estimator 2025, IRS tax brackets, tax refund calculator');
    }
  }, []);

  const [income, setIncome] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const [deductions, setDeductions] = useState('standard');
  const [itemizedAmount, setItemizedAmount] = useState('');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const standardDeductions: Record<string, number> = {
    single: 14600,
    married: 29200,
    head: 21900
  };

  const taxBrackets2026 = {
    single: [
      { rate: 0.10, max: 11600 },
      { rate: 0.12, max: 47150 },
      { rate: 0.22, max: 100525 },
      { rate: 0.24, max: 191950 },
      { rate: 0.32, max: 243725 },
      { rate: 0.35, max: 609350 },
      { rate: 0.37, max: Infinity }
    ],
    married: [
      { rate: 0.10, max: 23200 },
      { rate: 0.12, max: 94300 },
      { rate: 0.22, max: 201050 },
      { rate: 0.24, max: 383900 },
      { rate: 0.32, max: 487450 },
      { rate: 0.35, max: 731200 },
      { rate: 0.37, max: Infinity }
    ],
    head: [
      { rate: 0.10, max: 16550 },
      { rate: 0.12, max: 63100 },
      { rate: 0.22, max: 100500 },
      { rate: 0.24, max: 191950 },
      { rate: 0.32, max: 243700 },
      { rate: 0.35, max: 609350 },
      { rate: 0.37, max: Infinity }
    ]
  };

  const calculateTax = () => {
    const grossIncome = parseFloat(income);
    if (isNaN(grossIncome) || grossIncome <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    const deductionAmount = deductions === 'standard' 
      ? standardDeductions[filingStatus]
      : parseFloat(itemizedAmount) || 0;

    const taxableIncome = Math.max(0, grossIncome - deductionAmount);
    
    const brackets = taxBrackets2026[filingStatus as keyof typeof taxBrackets2026];
    let tax = 0;
    let previousMax = 0;

    for (const bracket of brackets) {
      if (taxableIncome > previousMax) {
        const taxableInBracket = Math.min(taxableIncome - previousMax, bracket.max - previousMax);
        tax += taxableInBracket * bracket.rate;
        previousMax = bracket.max;
      } else {
        break;
      }
    }

    const effectiveRate = (tax / grossIncome) * 100;

    setResult({
      grossIncome,
      deductionAmount,
      taxableIncome,
      totalTax: tax,
      effectiveRate,
      afterTaxIncome: grossIncome - tax
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Income Tax Estimator</h1>
            <p className="text-lg text-gray-600">Calculate your 2026 federal income tax liability based on your income, filing status, and deductions</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Gross Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="75000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Filing Status</label>
                  <select
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Filing Jointly</option>
                    <option value="head">Head of Household</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Deduction Type</label>
                  <select
                    value={deductions}
                    onChange={(e) => setDeductions(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="standard">Standard Deduction (${standardDeductions[filingStatus].toLocaleString()})</option>
                    <option value="itemized">Itemized Deductions</option>
                  </select>
                </div>

                {deductions === 'itemized' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Itemized Deduction Amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                      <input
                        type="number"
                        value={itemizedAmount}
                        onChange={(e) => setItemizedAmount(e.target.value)}
                        className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                        placeholder="20000"
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={calculateTax}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Calculate Tax
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Tax Calculation Results</h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-xl p-6">
                    <div className="text-sm text-teal-100 mb-1">Gross Income</div>
                    <div className="text-3xl font-bold">${result.grossIncome.toLocaleString()}</div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Deductions</span>
                      <span className="font-semibold">-${result.deductionAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Taxable Income</span>
                      <span className="font-semibold">${result.taxableIncome.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Total Tax</span>
                      <span className="font-semibold">${result.totalTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Effective Tax Rate</span>
                      <span className="font-semibold">{result.effectiveRate.toFixed(2)}%</span>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6 mt-6">
                    <div className="text-sm text-teal-100 mb-1">After-Tax Income</div>
                    <div className="text-3xl font-bold">${result.afterTaxIncome.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-teal-100">
                    <i className="ri-calculator-line text-6xl mb-4 opacity-50"></i>
                    <p>Enter your information and click Calculate Tax to see results</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">2026 Tax Brackets</h3>
            <p className="text-gray-600 mb-6">Understanding how tax brackets work can help you plan your finances better</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Single Filers</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>10%</span><span className="text-gray-600">Up to $11,600</span></div>
                  <div className="flex justify-between"><span>12%</span><span className="text-gray-600">$11,601 - $47,150</span></div>
                  <div className="flex justify-between"><span>22%</span><span className="text-gray-600">$47,151 - $100,525</span></div>
                  <div className="flex justify-between"><span>24%</span><span className="text-gray-600">$100,526 - $191,950</span></div>
                  <div className="flex justify-between"><span>32%</span><span className="text-gray-600">$191,951 - $243,725</span></div>
                  <div className="flex justify-between"><span>35%</span><span className="text-gray-600">$243,726 - $609,350</span></div>
                  <div className="flex justify-between"><span>37%</span><span className="text-gray-600">Over $609,350</span></div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Married Filing Jointly</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>10%</span><span className="text-gray-600">Up to $23,200</span></div>
                  <div className="flex justify-between"><span>12%</span><span className="text-gray-600">$23,201 - $94,300</span></div>
                  <div className="flex justify-between"><span>22%</span><span className="text-gray-600">$94,301 - $201,050</span></div>
                  <div className="flex justify-between"><span>24%</span><span className="text-gray-600">$201,051 - $383,900</span></div>
                  <div className="flex justify-between"><span>32%</span><span className="text-gray-600">$383,901 - $487,450</span></div>
                  <div className="flex justify-between"><span>35%</span><span className="text-gray-600">$487,451 - $731,200</span></div>
                  <div className="flex justify-between"><span>37%</span><span className="text-gray-600">Over $731,200</span></div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Head of Household</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>10%</span><span className="text-gray-600">Up to $16,550</span></div>
                  <div className="flex justify-between"><span>12%</span><span className="text-gray-600">$16,551 - $63,100</span></div>
                  <div className="flex justify-between"><span>22%</span><span className="text-gray-600">$63,101 - $100,500</span></div>
                  <div className="flex justify-between"><span>24%</span><span className="text-gray-600">$100,501 - $191,950</span></div>
                  <div className="flex justify-between"><span>32%</span><span className="text-gray-600">$191,951 - $243,700</span></div>
                  <div className="flex justify-between"><span>35%</span><span className="text-gray-600">$243,701 - $609,350</span></div>
                  <div className="flex justify-between"><span>37%</span><span className="text-gray-600">Over $609,350</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}